/**
 * What: Canonical centralized registry mapping semantic roles, marketing variant types,
 *   UI badges, classification explanations, core 3-question SSR prompts, and treatment contracts.
 * Transport / data-flow: Shared across Shopify compiler prompts, D1 catalog enrichment,
 *   Product Explorer UI, Study Builder seeding, and Study Planner execution.
 *
 */
import { imageConcept } from './product-research-image-concepts.js';
import { SHOPIFY_PRODUCT_CORE_QUESTION_TEXT, SHOPIFY_PACK_TEST_QUESTION_TEXT, SHOPIFY_OFFER_TEST_QUESTION_TEXT, } from './product-research-questions.js';
const DEFAULT_QUESTIONS = {
    version: 1,
    questionSetId: 'default-product-concept-v1',
    understanding: 'Please describe in 1-2 sentences what you understand this product or concept to be and what problem it solves.',
    purchase_intent: "Please explain in 1-2 sentences how likely you are to consider this product or concept when it's available.",
    impression: 'Please explain in 1-2 sentences your overall impression of this product or concept. What stands out most, positively or negatively?',
};
export const SHOPIFY_CONCEPT_REGISTRY = {
    claim: {
        semanticRole: 'claim',
        variantType: 'claim',
        eligibleForStudy: true,
        uiBadge: { label: 'Claim', tone: 'attention' },
        group: {
            key: 'claims',
            title: 'Claim',
            description: 'Product claims, brand statements, ingredient messages, and other product copy to compare.',
            order: 1,
        },
        classificationReason: 'Merchant-authored product language suitable for claim testing',
        coreQuestions: {
            version: 1,
            questionSetId: 'claim-core-questions-v1',
            understanding: "Please describe in 1-2 sentences what you understand this claim to be saying about the product. If anything is unclear, describe what's unclear.",
            purchase_intent: 'Please explain in 1-2 sentences how, if at all, this claim influences your likelihood to consider the product. What about the claim shapes that?',
            impression: 'Please explain in 1-2 sentences your overall impression of this claim. What stands out most, positively or negatively?',
        },
        contracts: {
            elementType: 'claim',
            stimulusContract: 'claim_in_context',
            treatmentContract: 'claim_variant',
        },
    },
    brand_tagline: {
        semanticRole: 'brand_tagline',
        variantType: 'brand_tagline',
        eligibleForStudy: false,
        uiBadge: { label: 'Brand Hook', tone: 'attention' },
        group: {
            key: 'brand_taglines',
            title: 'Brand Positioning & Slogans',
            description: 'Brand hooks, taglines, and positioning statements to test for resonance and memorability.',
            order: 2,
        },
        classificationReason: 'Brand hook, tagline, or positioning statement suitable for messaging testing',
        coreQuestions: {
            version: 1,
            questionSetId: 'brand-tagline-core-questions-v1',
            understanding: "Please describe in 1-2 sentences what you understand this brand statement or hook to communicate about the product. If anything is unclear, describe what's unclear.",
            purchase_intent: 'Please explain in 1-2 sentences how, if at all, this brand statement influences your likelihood to consider the product. What about it shapes that?',
            impression: 'Please explain in 1-2 sentences your overall impression of this hook or brand statement. What stands out most, positively or negatively?',
        },
        contracts: {
            elementType: 'claim',
            stimulusContract: 'claim_in_context',
            treatmentContract: 'claim_variant',
        },
    },
    taste_sensory: {
        semanticRole: 'taste_sensory',
        variantType: 'taste_sensory',
        eligibleForStudy: false,
        uiBadge: { label: 'Taste & Sensory', tone: 'success' },
        group: {
            key: 'taste_sensory',
            title: 'Taste, Scent & Sensory Notes',
            description: 'Flavour profiles, tasting notes, fragrance, or texture descriptions to test for sensory appeal.',
            order: 3,
        },
        classificationReason: 'Flavour profile, tasting notes, or sensory description suitable for concept testing',
        coreQuestions: {
            version: 1,
            questionSetId: 'taste-sensory-core-questions-v1',
            understanding: 'Please describe in 1-2 sentences what you understand this flavour or sensory profile to tell you about the product. If anything is unclear, describe what stands out.',
            purchase_intent: 'Please explain in 1-2 sentences how, if at all, this taste or sensory description influences your likelihood to consider the product. What about it shapes that?',
            impression: 'Please explain in 1-2 sentences your overall impression of this flavour and sensory description. What stands out most, positively or negatively?',
        },
        contracts: {
            elementType: 'claim',
            stimulusContract: 'claim_in_context',
            treatmentContract: 'claim_variant',
        },
    },
    ingredient: {
        semanticRole: 'ingredient',
        variantType: 'ingredient_proof',
        eligibleForStudy: false,
        uiBadge: { label: 'Science & Formula', tone: 'success' },
        group: {
            key: 'ingredients',
            title: 'Ingredients & Formulation Proof',
            description: 'Key ingredients, formulation science, or material composition to test as reasons-to-believe.',
            order: 4,
        },
        classificationReason: 'Ingredient science, formula details, or material composition suitable for proof testing',
        coreQuestions: {
            version: 1,
            questionSetId: 'ingredient-core-questions-v1',
            understanding: "Please describe in 1-2 sentences what you understand this specific ingredient, formula, or material to contribute to the product. If anything is unclear, describe what's unclear.",
            purchase_intent: 'Please explain in 1-2 sentences how, if at all, this ingredient science or formulation detail influences your likelihood to consider the product. What about it shapes that?',
            impression: 'Please explain in 1-2 sentences your overall impression of this ingredient or material proof. How credible and compelling does it feel to you?',
        },
        contracts: {
            elementType: 'ingredient_proof',
            stimulusContract: 'text_concept',
            treatmentContract: 'claim_variant',
        },
    },
    usage_routine: {
        semanticRole: 'usage_routine',
        variantType: 'usage_experience',
        eligibleForStudy: false,
        uiBadge: { label: 'Routine & Ritual', tone: 'info' },
        group: {
            key: 'usage_routines',
            title: 'Routine, Ritual & Preparation',
            description: 'Directions for use, serving rituals, preparation steps, or maintenance routines to test for convenience and clarity.',
            order: 5,
        },
        classificationReason: 'Usage directions, serving rituals, or care instructions suitable for routine testing',
        coreQuestions: {
            version: 1,
            questionSetId: 'usage-routine-core-questions-v1',
            understanding: "Please describe in 1-2 sentences how clearly you understand the steps, ritual, or routine required to use, prepare, or care for this product. If anything is unclear, describe what's unclear.",
            purchase_intent: 'Please explain in 1-2 sentences how, if at all, this usage routine, serving ritual, or care process influences your likelihood to consider the product. What about the routine shapes that?',
            impression: 'Please explain in 1-2 sentences your overall impression of how this product is used, prepared, or maintained. What stands out most regarding convenience or routine fit?',
        },
        contracts: {
            elementType: 'usage_experience',
            stimulusContract: 'text_concept',
            treatmentContract: 'claim_variant',
        },
    },
    offer: {
        semanticRole: 'offer',
        variantType: 'offer',
        eligibleForStudy: true,
        uiBadge: { label: 'Offer', tone: 'warning' },
        group: {
            key: 'offers',
            title: 'Offer',
            description: 'Offer terms, including promotions and bundles, compared as complete offers.',
            order: 6,
        },
        classificationReason: 'Merchant-authored commercial offer, bundle, or promotion suitable for offer testing',
        coreQuestions: {
            version: 2,
            questionSetId: 'offer-test-core-questions-v1',
            ...SHOPIFY_OFFER_TEST_QUESTION_TEXT,
        },
        scoring: { anchorSet: 'offer-test-v1', primaryConstruct: 'purchase_intent', requiresImage: false },
        contracts: {
            elementType: 'promotion_mechanic',
            stimulusContract: 'offer_row',
            treatmentContract: 'mixed_offer_row',
        },
    },
    price: {
        semanticRole: 'price',
        variantType: 'price',
        eligibleForStudy: true,
        uiBadge: { label: 'Price', tone: 'info' },
        group: {
            key: 'pricing',
            title: 'Price',
            description: "The selected product variant's price, compared against alternative prices.",
            order: 7,
        },
        classificationReason: 'Price point, subscription terms, or financing options suitable for price testing',
        coreQuestions: {
            version: 1,
            questionSetId: 'price-core-questions-v1',
            ...SHOPIFY_PRODUCT_CORE_QUESTION_TEXT,
        },
        contracts: {
            elementType: 'price',
            stimulusContract: 'price_in_context',
            treatmentContract: 'price_variant',
        },
    },
    image: {
        semanticRole: 'image',
        variantType: 'image',
        eligibleForStudy: true,
        uiBadge: { label: 'Photo', tone: 'neutral' },
        group: {
            key: 'images',
            title: 'Photo',
            description: 'Product images, including packaging, instructions, and advertising imagery.',
            order: 8,
        },
        classificationReason: 'Product imagery, pack shot, or creative concept suitable for visual testing',
        coreQuestions: {
            version: 2,
            questionSetId: 'image-core-questions-v2',
            ...SHOPIFY_PRODUCT_CORE_QUESTION_TEXT,
        },
        contracts: {
            elementType: 'image_creative',
            stimulusContract: 'visual_concept',
            treatmentContract: 'image_variant',
        },
    },
    packaging: {
        ...imageConcept('packaging', 'Packaging', 'Compare packaging images shown with the product.', 14),
        coreQuestions: {
            version: 3,
            questionSetId: 'pack-test-core-questions-v2',
            ...SHOPIFY_PACK_TEST_QUESTION_TEXT,
        },
        scoring: { anchorSet: 'pack-test-v1', primaryConstruct: 'impression', requiresImage: true },
        // The required communication question now covers this probe. Preserve already-authored diagnostics, without suggesting a duplicate.
        diagnosticQuestions: [],
    },
    instructions: imageConcept('instructions', 'Instructions', 'Compare images that explain how to use, prepare, or care for the product.', 15),
    advertising: imageConcept('advertising', 'Advertising', 'Compare advertising images shown with the product.', 16),
    description: {
        semanticRole: 'description',
        variantType: 'description',
        eligibleForStudy: true,
        uiBadge: { label: 'Description', tone: 'info' },
        group: {
            key: 'descriptions',
            title: 'Description',
            description: 'The product description, compared against complete replacement descriptions.',
            order: 13,
        },
        classificationReason: 'Product description can be tested as a complete replacement description',
        coreQuestions: {
            version: 1,
            questionSetId: 'description-core-questions-v1',
            ...SHOPIFY_PRODUCT_CORE_QUESTION_TEXT,
        },
        contracts: {
            elementType: 'description',
            stimulusContract: 'text_concept',
            treatmentContract: 'description_variant',
        },
    },
    target_audience: {
        semanticRole: 'target_audience',
        variantType: null,
        eligibleForStudy: false,
        uiBadge: { label: 'Audience Profile', tone: 'neutral' },
        group: {
            key: 'audience_context',
            title: 'Target Customer Context',
            description: 'Target audience specifications and persona context used for respondent sampling.',
            order: 9,
        },
        classificationReason: 'Target customer segment or persona definition used for context, not direct stimuli',
        coreQuestions: DEFAULT_QUESTIONS,
    },
    social_proof: {
        semanticRole: 'social_proof',
        variantType: null,
        eligibleForStudy: false,
        uiBadge: { label: 'Social Proof', tone: 'neutral' },
        group: {
            key: 'social_proof',
            title: 'Social Proof & Accolades',
            description: 'Awards, certifications, press mentions, or customer accolades.',
            order: 10,
        },
        classificationReason: 'Press accolades, awards, or customer reviews used for social proof',
        coreQuestions: DEFAULT_QUESTIONS,
    },
    technical_spec: {
        semanticRole: 'technical_spec',
        variantType: null,
        eligibleForStudy: false,
        uiBadge: { label: 'Technical Spec', tone: 'neutral' },
        group: {
            key: 'technical_specs',
            title: 'Technical Specifications',
            description: 'Dimensions, weights, regulatory identifiers, or engineering specifications.',
            order: 11,
        },
        classificationReason: 'Technical specification, dimensions, or regulatory details',
        coreQuestions: DEFAULT_QUESTIONS,
    },
    unusable_internal: {
        semanticRole: 'unusable_internal',
        variantType: null,
        eligibleForStudy: false,
        uiBadge: { label: 'System Internal', tone: 'neutral' },
        group: {
            key: 'internal',
            title: 'System & Internal Metadata',
            description: 'Internal IDs, sync hashes, theme keys, or third-party app metadata excluded from studies.',
            order: 12,
        },
        classificationReason: 'Internal system metadata excluded from research studies',
        coreQuestions: DEFAULT_QUESTIONS,
    },
};
/** Returns the concept definition for a given semantic role, falling back to technical_spec if unknown. */
export function getConceptDefinitionBySemanticRole(role) {
    if (role && role in SHOPIFY_CONCEPT_REGISTRY) {
        return SHOPIFY_CONCEPT_REGISTRY[role];
    }
    return SHOPIFY_CONCEPT_REGISTRY.technical_spec;
}
/** Returns the concept definition for a given marketing variant type, falling back to claim if unknown. */
export function getConceptDefinitionByVariantType(type) {
    if (!type)
        return SHOPIFY_CONCEPT_REGISTRY.claim;
    const match = Object.values(SHOPIFY_CONCEPT_REGISTRY).find((def) => def.variantType === type);
    return match ?? SHOPIFY_CONCEPT_REGISTRY.claim;
}
/** Returns all concept groups sorted in their canonical display order. */
export function getOrderedConceptGroups() {
    const groups = Object.values(SHOPIFY_CONCEPT_REGISTRY).map((def) => def.group);
    const uniqueByKey = new Map();
    for (const group of groups) {
        if (!uniqueByKey.has(group.key)) {
            uniqueByKey.set(group.key, group);
        }
    }
    return Array.from(uniqueByKey.values()).sort((a, b) => a.order - b.order);
}
/** Shared image capability: source validation and authoring use the registry's physical treatment contract. */
export function isShopifyImageConcept(type) {
    return getConceptDefinitionByVariantType(type).contracts?.treatmentContract === 'image_variant';
}
//# sourceMappingURL=product-research-registry.js.map